import { Dialog } from "@excalidraw/excalidraw/components/Dialog";
import { CaptureUpdateAction, loadFromBlob } from "@excalidraw/excalidraw";
import { useEffect, useState } from "react";

import type { ExcalidrawImperativeAPI } from "@excalidraw/excalidraw/types";

import { atom, useAtom, useAtomValue, useSetAtom } from "../app-jotai";
import { getDiagramLink, getDiagramMuidFromUrl } from "../data";
import { fetchMe, meAtom } from "../data/me";

// The Open dialog: switch workspace and browse / create / rename the diagrams in
// it, plus a secondary "Load from file" escape hatch. Each diagram is addressed
// by its base-62 MUID; "opening" one navigates to /d/<muid>, which the app
// connects to its DiagramRoom. Account state (sign in/out) lives in the top-right
// AccountChip, not here. Anonymous visitors get a sign-in prompt instead of the
// workspace UI (which would 401). (Folder organization within a workspace is a
// later iteration; the diagram list is the seam for it.)
export const diagramsDialogAtom = atom<boolean>(false);

interface DiagramMeta {
  muid: string;
  name: string;
  updatedAt: number;
}

const jsonFetch = async (url: string, init?: RequestInit) => {
  const res = await fetch(url, init);
  if (!res.ok) {
    throw new Error(`${init?.method ?? "GET"} ${url} → HTTP ${res.status}`);
  }
  return res.json();
};

export const DiagramsDialog = ({
  excalidrawAPI,
}: {
  excalidrawAPI: ExcalidrawImperativeAPI | null;
}) => {
  const [open, setOpen] = useAtom(diagramsDialogAtom);
  const me = useAtomValue(meAtom);
  const setMe = useSetAtom(meAtom);
  const [diagrams, setDiagrams] = useState<DiagramMeta[]>([]);
  const [loading, setLoading] = useState(false);
  const [newName, setNewName] = useState("");
  const [error, setError] = useState<string | null>(null);

  // Refresh the shared account state + this workspace's diagram list. Anonymous
  // visitors have no server diagrams, so we skip the (401-ing) list fetch.
  const refresh = async () => {
    setLoading(true);
    setError(null);
    try {
      const meData = await fetchMe();
      setMe(meData);
      if (meData?.user) {
        setDiagrams((await jsonFetch("/api/diagrams")) as DiagramMeta[]);
      } else {
        setDiagrams([]);
      }
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) {
      refresh();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const openDiagram = (muid: string) => {
    window.location.href = getDiagramLink(muid);
  };

  const createDiagram = async () => {
    try {
      const created = (await jsonFetch("/api/diagrams", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ name: newName.trim() || "Untitled" }),
      })) as DiagramMeta;
      openDiagram(created.muid);
    } catch (err: any) {
      setError(err.message);
    }
  };

  // Secondary path: replace the current scene with an .excalidraw file (or an
  // image with an embedded scene) picked from disk. Mirrors the native
  // load-from-file, but lives inside the Open dialog instead of a standalone
  // overwrite screen.
  const loadFromFile = () => {
    if (!excalidrawAPI) {
      return;
    }
    const input = document.createElement("input");
    input.type = "file";
    input.accept = ".excalidraw,application/json,image/png,image/svg+xml";
    input.onchange = async () => {
      const file = input.files?.[0];
      if (!file) {
        return;
      }
      try {
        const contents = await loadFromBlob(
          file,
          excalidrawAPI.getAppState(),
          excalidrawAPI.getSceneElements(),
        );
        excalidrawAPI.updateScene({
          elements: contents.elements,
          appState: contents.appState,
          captureUpdate: CaptureUpdateAction.IMMEDIATELY,
        });
        if (contents.files) {
          excalidrawAPI.addFiles(Object.values(contents.files));
        }
        setOpen(false);
      } catch (err: any) {
        setError(err.message);
      }
    };
    input.click();
  };

  const renameDiagram = async (muid: string, current: string) => {
    const name = window.prompt("Rename diagram", current);
    if (!name || name.trim() === current) {
      return;
    }
    try {
      await jsonFetch(`/api/diagrams/${muid}`, {
        method: "PATCH",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ name: name.trim() }),
      });
      // If the renamed diagram is the one on screen, keep the tab title current.
      if (muid === getDiagramMuidFromUrl()) {
        document.title = `${name.trim()} - Excalidraw`;
      }
      await refresh();
    } catch (err: any) {
      setError(err.message);
    }
  };

  const switchWorkspace = async (workspaceId: number) => {
    try {
      await jsonFetch("/api/workspaces/switch", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ workspaceId }),
      });
      await refresh();
    } catch (err: any) {
      setError(err.message);
    }
  };

  const setHistoryMode = async (mode: string) => {
    if (!me?.activeWorkspaceId) {
      return;
    }
    try {
      await jsonFetch(`/api/workspaces/${me.activeWorkspaceId}`, {
        method: "PATCH",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ historyMode: mode }),
      });
      await refresh();
    } catch (err: any) {
      setError(err.message);
    }
  };

  const createWorkspace = async () => {
    const name = window.prompt("New workspace name");
    if (!name?.trim()) {
      return;
    }
    try {
      const ws = (await jsonFetch("/api/workspaces", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ name: name.trim() }),
      })) as { id: number };
      await switchWorkspace(ws.id);
    } catch (err: any) {
      setError(err.message);
    }
  };

  if (!open) {
    return null;
  }

  const activeMuid = getDiagramMuidFromUrl();
  const hasIdentity = !!me?.user;
  const activeWorkspace = me?.workspaces.find(
    (w) => w.id === me.activeWorkspaceId,
  );

  return (
    <Dialog
      onCloseRequest={() => setOpen(false)}
      title="Open diagram"
      size="small"
    >
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: "1rem",
          paddingBottom: "1rem",
        }}
      >
        {loading && !me ? (
          <div style={{ opacity: 0.6 }}>Loading…</div>
        ) : !hasIdentity ? (
          // Anonymous: no server workspace to browse. Invite sign-in; they can
          // still load a file into the local canvas below.
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "flex-start",
              gap: ".75rem",
              paddingBottom: ".75rem",
              borderBottom: "1px solid var(--default-border-color, #e3e2e4)",
            }}
          >
            <div style={{ fontSize: 14, lineHeight: 1.5 }}>
              Sign in to save diagrams to your workspace, organize them, and
              collaborate. You can keep drawing without an account — your work
              stays in this browser until you save it to disk or sign in.
            </div>
            <button
              className="excalidraw-button"
              style={{ padding: "0 1rem", height: "2.25rem", fontWeight: 600 }}
              onClick={() => {
                window.location.href = "/api/auth/login";
              }}
            >
              Sign in with Google
            </button>
          </div>
        ) : (
          <>
            {/* workspace + history */}
            <div
              style={{
                display: "flex",
                alignItems: "center",
                flexWrap: "wrap",
                gap: ".5rem",
                paddingBottom: ".75rem",
                borderBottom: "1px solid var(--default-border-color, #e3e2e4)",
              }}
            >
              <span style={{ opacity: 0.6, fontSize: 13 }}>Workspace</span>
              <select
                value={me?.activeWorkspaceId ?? undefined}
                onChange={(e) => switchWorkspace(Number(e.target.value))}
                style={{ padding: ".25rem .4rem", borderRadius: 6 }}
              >
                {me?.workspaces.map((w) => (
                  <option key={w.id} value={w.id}>
                    {w.name}
                  </option>
                ))}
              </select>
              <button
                onClick={createWorkspace}
                title="New workspace"
                style={{
                  background: "none",
                  border: "none",
                  cursor: "pointer",
                }}
              >
                +
              </button>
              {activeWorkspace && (
                <>
                  <span
                    style={{ opacity: 0.6, fontSize: 13, marginLeft: ".5rem" }}
                  >
                    History
                  </span>
                  <select
                    value={activeWorkspace.historyMode}
                    onChange={(e) => setHistoryMode(e.target.value)}
                    title="How versions are captured for diagrams in this workspace"
                    style={{ padding: ".25rem .4rem", borderRadius: 6 }}
                  >
                    <option value="checkpoint">Checkpoint (manual)</option>
                    <option value="continuous">Continuous (auto)</option>
                  </select>
                </>
              )}
            </div>

            {/* create */}
            <div style={{ display: "flex", gap: ".5rem" }}>
              <input
                className="ExcTextField__input"
                style={{ flex: 1 }}
                value={newName}
                placeholder="New diagram name"
                onChange={(e) => setNewName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    createDiagram();
                  }
                }}
              />
              <button
                className="excalidraw-button"
                style={{ padding: "0 1rem", fontWeight: 600 }}
                onClick={createDiagram}
              >
                Create
              </button>
            </div>

            {/* list */}
            {loading ? (
              <div style={{ opacity: 0.6 }}>Loading…</div>
            ) : (
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: ".25rem",
                  maxHeight: 320,
                  overflowY: "auto",
                }}
              >
                {diagrams.length === 0 && (
                  <div style={{ opacity: 0.6 }}>
                    No diagrams yet — create one.
                  </div>
                )}
                {diagrams.map((d) => {
                  const isActive = activeMuid === d.muid;
                  return (
                    <div
                      key={d.muid}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: ".5rem",
                        padding: ".4rem .6rem",
                        borderRadius: 8,
                        background: isActive
                          ? "var(--color-surface-primary-container)"
                          : "transparent",
                      }}
                    >
                      <button
                        onClick={() => openDiagram(d.muid)}
                        style={{
                          flex: 1,
                          textAlign: "left",
                          background: "none",
                          border: "none",
                          cursor: "pointer",
                          fontSize: 14,
                          fontWeight: isActive ? 700 : 400,
                        }}
                      >
                        {d.name}
                      </button>
                      <button
                        onClick={() => renameDiagram(d.muid, d.name)}
                        style={{
                          background: "none",
                          border: "none",
                          cursor: "pointer",
                          opacity: 0.7,
                          fontSize: 13,
                        }}
                      >
                        Rename
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
          </>
        )}

        {error && <div style={{ color: "var(--color-danger)" }}>{error}</div>}

        {/* secondary: load from a local file (replaces the current scene) */}
        {excalidrawAPI && (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: ".75rem",
              paddingTop: ".75rem",
              marginTop: ".25rem",
              borderTop: "1px solid var(--default-border-color, #e3e2e4)",
            }}
          >
            <span style={{ opacity: 0.6, fontSize: 12 }}>
              Loading from a file replaces the current scene.
            </span>
            <button
              className="excalidraw-button"
              style={{
                padding: "0 1rem",
                fontWeight: 600,
                whiteSpace: "nowrap",
              }}
              onClick={loadFromFile}
            >
              Load from file
            </button>
          </div>
        )}
      </div>
    </Dialog>
  );
};
